// @ts-nocheck
import { PopIcon } from './icons/PopIcons';
// ==========================================
// SeasonalEvent - Composant d'affichage des événements saisonniers
// TODO: Fix types when seasonal events are properly implemented
// ==========================================

import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuthStore } from '../stores/authStore';
// Types defined locally since they're not in shared types yet
interface EventReward {
  id: string;
  name: string;
  type: string;
  rarity: string;
  icon: string;
}

interface CurrentEventData {
  id: string;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  theme: string;
  rewards: EventReward[];
  progress?: number;
  total?: number;
}

// ==========================================
// Hook pour récupérer l'event en cours
// ==========================================
export const useSeasonalEvent = () => {
  const [eventData, setEventData] = useState<CurrentEventData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { token } = useAuthStore();

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const headers: HeadersInit = {};
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
        
        const res = await fetch('/api/events/current', { headers });
        const data = await res.json();
        
        if (data.success && data.data) {
          setEventData(data.data);
        } else {
          setEventData(null);
        }
      } catch (err) {
        setError('Erreur de chargement');
        console.error('Erreur fetch event:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
    
    // Rafraîchir toutes les 5 minutes
    const interval = setInterval(fetchEvent, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [token]);

  return { eventData, loading, error, refetch: () => setLoading(true) };
};

// ==========================================
// Bannière d'event (à afficher en haut de page)
// ==========================================
// Mapping des icônes texte vers les emojis
// ==========================================
const iconMap: Record<string, string> = {
  pumpkin: '🎃',
  tree: '🎄',
  star: '⭐',
  gamepad: '🕹️',
  celebration: '🎉',
  // Fallback: si l'icône est déjà un emoji, on la garde
};

const getEventIcon = (icon: string): string => {
  return iconMap[icon] || icon || '🎉';
};

// ==========================================
interface EventBannerProps {
  eventData: CurrentEventData;
  onOpenDetails?: () => void;
  compact?: boolean;
}

export const EventBanner: React.FC<EventBannerProps> = ({ 
  eventData, 
  onOpenDetails,
  compact = false 
}) => {
  const { event, remainingDays, progress } = eventData;
  const eventIcon = getEventIcon(event.icon);
  
  // Calculer la progression globale
  const totalRewards = eventData.rewards.length;
  const claimedRewards = progress?.rewards_claimed.length || 0;
  const progressPercent = totalRewards > 0 ? (claimedRewards / totalRewards) * 100 : 0;

  if (compact) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="cursor-pointer"
        onClick={onOpenDetails}
        style={{
          background: `linear-gradient(135deg, ${event.primary_color}40, ${event.secondary_color}40)`,
          borderColor: event.primary_color,
        }}
        whileHover={{ scale: 1.02 }}
      >
        <div className="flex items-center gap-2 px-3 py-1 rounded-full border text-sm">
          <span className="text-lg"><PopIcon name={eventIcon}/></span>
          <span className="font-medium text-white">{event.name}</span>
          <span className="text-white/70 text-xs">
            {remainingDays}j restant{remainingDays > 1 ? 's' : ''}
          </span>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-xl cursor-pointer"
      onClick={onOpenDetails}
      style={{
        background: `linear-gradient(135deg, ${event.primary_color}, ${event.secondary_color})`,
      }}
      whileHover={{ scale: 1.01 }}
    >
      {/* Effet de brillance */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full animate-[shimmer_3s_infinite]" />
      
      <div className="relative z-10 p-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="text-4xl"><PopIcon name={eventIcon}/></div>
          <div>
            <h3 className="text-xl font-bold text-white">{event.name}</h3>
            <p className="text-white/80 text-sm">{event.description}</p>
          </div>
        </div>
        
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-2">
            <span className="text-white/70 text-sm">XP ×{event.xp_multiplier}</span>
            <span className="bg-white/20 px-2 py-1 rounded text-white text-sm font-medium">
              <PopIcon name="⏱️"/> {remainingDays} jour{remainingDays > 1 ? 's' : ''}
            </span>
          </div>
          
          {progress && (
            <div className="flex items-center gap-2 text-sm text-white/80">
              <span><PopIcon name="🎁"/> {claimedRewards}/{totalRewards}</span>
              <div className="w-24 h-2 bg-white/20 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-white/60 rounded-full transition-all"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// ==========================================
// Modal détails de l'event
// ==========================================
interface EventDetailsModalProps {
  eventData: CurrentEventData;
  isOpen: boolean;
  onClose: () => void;
  onClaimReward: (rewardId: number) => Promise<void>;
}

export const EventDetailsModal: React.FC<EventDetailsModalProps> = ({
  eventData,
  isOpen,
  onClose,
  onClaimReward,
}) => {
  const { event, rewards, progress, remainingMs } = eventData;
  const eventIcon = getEventIcon(event.icon);
  const [claiming, setClaiming] = useState<number | null>(null);

  // Calculer le compte à rebours
  const [timeLeft, setTimeLeft] = useState(remainingMs);
  
  useEffect(() => {
    if (!isOpen) return;
    
    const interval = setInterval(() => {
      setTimeLeft(prev => Math.max(0, prev - 1000));
    }, 1000);
    
    return () => clearInterval(interval);
  }, [isOpen, remainingMs]);

  const formatTime = (ms: number) => {
    const days = Math.floor(ms / (1000 * 60 * 60 * 24));
    const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((ms % (1000 * 60)) / 1000);
    
    if (days > 0) return `${days}j ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    return `${minutes}m ${seconds}s`;
  };

  const handleClaim = async (rewardId: number) => {
    setClaiming(rewardId);
    try {
      await onClaimReward(rewardId);
    } finally {
      setClaiming(null);
    }
  };

  const getRewardIcon = (reward: EventReward) => {
    switch (reward.reward_type) {
      case 'coins': return '🪙';
      case 'lootbox': return '📦';
      case 'cosmetic': return '🎨';
      case 'title': return '🏷️';
      case 'badge': return '🎖️';
      default: return '🎁';
    }
  };

  const getProgressValue = (requirementType: string): number => {
    if (!progress) return 0;
    switch (requirementType) {
      case 'games_played': return progress.games_played;
      case 'games_won': return progress.games_won;
      case 'correct_answers': return progress.correct_answers;
      case 'score_total': return progress.score_total;
      case 'login_days': return progress.login_days;
      default: return 0;
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="relative w-full max-w-2xl max-h-[85vh] overflow-hidden rounded-2xl"
          style={{
            background: `linear-gradient(180deg, ${event.primary_color}30 0%, #1a1a2e 100%)`,
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div 
            className="relative p-6 pb-4"
            style={{
              background: `linear-gradient(135deg, ${event.primary_color}, ${event.secondary_color})`,
            }}
          >
            <button
              onClick={onClose}
            aria-label="Fermer"
            className="absolute top-4 right-4 text-white/80 hover:text-white text-2xl"
            >
              <PopIcon name="✕"/>
            </button>
            
            <div className="flex items-center gap-4">
              <div className="text-6xl"><PopIcon name={eventIcon}/></div>
              <div>
                <h2 className="text-2xl font-bold text-white">{event.name}</h2>
                <p className="text-white/80">{event.description}</p>
              </div>
            </div>
            
            {/* Compte à rebours */}
            <div className="mt-4 flex items-center justify-center gap-4 p-3 bg-black/20 rounded-lg">
              <span className="text-white/80">Temps restant:</span>
              <span className="text-xl font-bold text-white font-mono">
                {formatTime(timeLeft)}
              </span>
            </div>
          </div>

          {/* Stats de l'event */}
          {progress && (
            <div className="grid grid-cols-5 gap-2 p-4 border-b border-white/10">
              <div className="text-center">
                <div className="text-lg font-bold text-white">{progress.games_played}</div>
                <div className="text-xs text-white/60">Parties</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-white">{progress.games_won}</div>
                <div className="text-xs text-white/60">Victoires</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-white">{progress.correct_answers}</div>
                <div className="text-xs text-white/60">Réponses</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-white">{progress.score_total}</div>
                <div className="text-xs text-white/60">Score</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-white">{progress.login_days}</div>
                <div className="text-xs text-white/60">Jours</div>
              </div>
            </div>
          )}

          {/* Liste des récompenses */}
          <div className="p-4 overflow-y-auto max-h-[40vh] space-y-3">
            <h3 className="text-lg font-semibold text-white mb-3"><PopIcon name="🎁"/> Récompenses</h3>
            
            {rewards.map(reward => {
              const currentValue = getProgressValue(reward.requirement_type);
              const isCompleted = currentValue >= reward.requirement_value;
              const isClaimed = progress?.rewards_claimed.includes(reward.id);
              const progressPercent = Math.min(100, (currentValue / reward.requirement_value) * 100);
              
              return (
                <motion.div
                  key={reward.id}
                  className={`relative p-4 rounded-xl border transition-all ${
                    isClaimed 
                      ? 'bg-green-500/10 border-green-500/30' 
                      : isCompleted 
                        ? 'bg-yellow-500/10 border-yellow-500/50 cursor-pointer hover:bg-yellow-500/20' 
                        : 'bg-white/5 border-white/10'
                  }`}
                  whileHover={isCompleted && !isClaimed ? { scale: 1.02 } : {}}
                  onClick={() => isCompleted && !isClaimed && handleClaim(reward.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl"><PopIcon name={getRewardIcon(reward)}/></span>
                      <div>
                        <p className="text-white font-medium">{reward.description}</p>
                        <p className="text-white/60 text-sm">
                          {reward.reward_type === 'coins' 
                            ? `${reward.coins_amount} coins` 
                            : reward.reward_type === 'lootbox'
                              ? 'Coffre exclusif'
                              : 'Récompense exclusive'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-end gap-1">
                      {isClaimed ? (
                        <span className="text-green-400 font-medium"><PopIcon name="✓"/> Réclamé</span>
                      ) : isCompleted ? (
                        <motion.button
                          className="px-4 py-2 bg-yellow-500 text-black font-bold rounded-lg"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          disabled={claiming === reward.id}
                        >
                          {claiming === reward.id ? '...' : 'Réclamer!'}
                        </motion.button>
                      ) : (
                        <span className="text-white/60 text-sm">
                          {currentValue}/{reward.requirement_value}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Barre de progression */}
                  {!isClaimed && (
                    <div className="mt-2 h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full rounded-full"
                        style={{
                          background: isCompleted 
                            ? 'linear-gradient(90deg, #f59e0b, #fbbf24)'
                            : `linear-gradient(90deg, ${event.primary_color}, ${event.secondary_color})`,
                          width: `${progressPercent}%`,
                        }}
                        initial={{ width: 0 }}
                        animate={{ width: `${progressPercent}%` }}
                        transition={{ duration: 0.5 }}
                      />
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>

          {/* Footer avec XP multiplier */}
          <div className="p-4 border-t border-white/10 text-center">
            <p className="text-white/70">
              Bonus d'XP actif: <span className="text-yellow-400 font-bold">×{event.xp_multiplier}</span> sur toutes les parties!
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ==========================================
// Effets visuels de fond selon le thème
// ==========================================
interface SeasonalBackgroundProps {
  effect: 'pumpkins' | 'snow' | 'stars' | 'pixels' | null;
  primaryColor: string;
}

export const SeasonalBackground: React.FC<SeasonalBackgroundProps> = ({ 
  effect,
  primaryColor 
}) => {
  // Générer des particules
  const particles = useMemo(() => {
    if (!effect) return [];
    
    const count = effect === 'snow' ? 50 : effect === 'stars' ? 40 : 25;
    const emojis: Record<string, string[]> = {
      pumpkins: ['🎃', '👻', '🦇', '💀', '🕷️', '🕸️'],
      snow: ['❄️', '❅', '❆', '✨'],
      stars: ['⭐', '✦', '✧', '💫', '🌟'],
      pixels: ['🕹️', '👾', '🎮', '💾', '🔴', '🟢', '🔵'],
    };
    
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 0.8 + Math.random() * 1.2,
      duration: 10 + Math.random() * 20,
      delay: Math.random() * -20,
      rotation: Math.random() * 360,
      emoji: emojis[effect]?.[Math.floor(Math.random() * emojis[effect].length)],
    }));
  }, [effect]);

  if (!effect || particles.length === 0) return null;

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      {particles.map(p => (
        <motion.div
          key={p.id}
          className="absolute text-lg select-none"
          style={{
            left: `${p.x}%`,
            fontSize: `${p.size}rem`,
            opacity: effect === 'stars' ? 0.7 : 0.5,
          }}
          initial={{
            y: effect === 'snow' ? '-10%' : `${p.y}%`,
            rotate: p.rotation,
          }}
          animate={
            effect === 'snow'
              ? {
                  y: '110vh',
                  x: [0, 20, -20, 10, 0],
                }
              : effect === 'stars'
                ? {
                    opacity: [0.3, 1, 0.3],
                    scale: [0.8, 1.2, 0.8],
                  }
                : {
                    y: [`${p.y}%`, `${p.y - 5}%`, `${p.y}%`],
                    rotate: [p.rotation, p.rotation + 10, p.rotation],
                  }
          }
          transition={{
            duration: p.duration,
            delay: p.delay,
            repeat: Infinity,
            ease: 'linear',
          }}
        >
          <PopIcon name={p.emoji}/>
        </motion.div>
      ))}
      
      {/* Effet de gradient overlay */}
      <div 
        className="absolute inset-0"
        style={{
          background: `radial-gradient(circle at 50% 0%, ${primaryColor}10 0%, transparent 50%)`,
        }}
      />
    </div>
  );
};

// ==========================================
// Wrapper pour appliquer le thème d'event
// ==========================================
interface SeasonalThemeWrapperProps {
  children: React.ReactNode;
}

export const SeasonalThemeWrapper: React.FC<SeasonalThemeWrapperProps> = ({ children }) => {
  const { eventData } = useSeasonalEvent();

  return (
    <>
      {eventData?.event && (
        <SeasonalBackground 
          effect={eventData.event.background_effect}
          primaryColor={eventData.event.primary_color}
        />
      )}
      {children}
    </>
  );
};

export default EventBanner;

